
import { useState } from 'react'
import { Calendar, Users, TrendingUp } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useAllCalendarAssignments, useUpdateCalendarAssignment } from '@/api/diet-plans-v2'
import { format } from 'date-fns'
import { PageHeader } from '@/components/shared/page-header'
import { TableEmptyRow, TableLoadingRows } from '@/components/shared/table-states'

export default function DietCalendarPage() {
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const { data: assignments = [], isLoading } = useAllCalendarAssignments()
  const updateMutation = useUpdateCalendarAssignment()

  const filteredAssignments = statusFilter === 'all' 
    ? assignments 
    : assignments.filter(a => a.status === statusFilter)

  const handleStatusChange = async (id: string, status: 'active' | 'completed' | 'cancelled') => {
    await updateMutation.mutateAsync({ id, status })
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return (
          <Badge
            variant="outline"
            className="border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-400"
          >
            Active
          </Badge>
        )
      case 'completed':
        return (
          <Badge
            variant="outline"
            className="border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-900 dark:bg-blue-950/40 dark:text-blue-400"
          >
            Completed
          </Badge>
        )
      case 'cancelled':
        return <Badge variant="secondary">Cancelled</Badge>
      default:
        return <Badge>{status}</Badge>
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Diet Calendar"
        description="View and manage all diet plan assignments"
      />

      {/* Stats Cards */}
      <div className="grid gap-4 grid-cols-1 sm:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Assignments</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {assignments.filter(a => a.status === 'active').length}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Completed This Month</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {assignments.filter(a => a.status === 'completed').length}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Users</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {new Set(assignments.map(a => a.userId)).size}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
            <label className="text-sm font-medium">Filter by Status:</label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Assignments Table */}
      <Card>
        <CardHeader>
          <CardTitle>All Assignments ({filteredAssignments.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Diet Plan</TableHead>
                <TableHead>Start Date</TableHead>
                <TableHead>End Date</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Progress</TableHead>
                <TableHead>Assigned By</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableLoadingRows colSpan={8} />
              ) : filteredAssignments.length === 0 ? (
                <TableEmptyRow
                  colSpan={8}
                  message="No assignments found. Create diet plans and assign them to users!"
                />
              ) : (
                filteredAssignments.map((assignment) => (
                    <TableRow key={assignment._id}>
                      <TableCell>
                        <div>
                          <div className="font-medium">
                            {typeof assignment.userId === 'object' && assignment.userId !== null
                              ? (() => { const u = assignment.userId as { name?: string; surname?: string }; return `${u.name ?? ''} ${u.surname ?? ''}`.trim() || assignment.userName; })()
                              : assignment.userName}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {typeof assignment.userId === 'object' && assignment.userId !== null
                              ? (assignment.userId as { email?: string }).email
                              : assignment.userEmail}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="font-medium">
                        {typeof assignment.dietPlanId === 'object' && assignment.dietPlanId !== null
                          ? (assignment.dietPlanId as { name?: string }).name
                          : assignment.dietPlanName}
                      </TableCell>
                      <TableCell>{format(new Date(assignment.startDate), 'PP')}</TableCell>
                      <TableCell>{format(new Date(assignment.endDate), 'PP')}</TableCell>
                      <TableCell>{getStatusBadge(assignment.status)}</TableCell>
                      <TableCell>
                        {assignment.progress && (
                          <div className="space-y-1">
                            <div className="text-sm">
                              {assignment.progress.daysCompleted} / {assignment.progress.totalDays} days
                            </div>
                            <div className="text-sm text-muted-foreground">
                              {assignment.progress.adherenceRate}% adherence
                            </div>
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        {typeof assignment.assignedBy === 'object' 
                          ? 'Staff' 
                          : assignment.assignedByName}
                      </TableCell>
                      <TableCell>
                        {assignment.status === 'active' && (
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleStatusChange(assignment._id, 'completed')}
                            >
                              Complete
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleStatusChange(assignment._id, 'cancelled')}
                            >
                              Cancel
                            </Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  )
}
